import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Radio,
} from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { useTheme } from '@material-ui/core/styles';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { FilterParamsItemType } from '@src/reducers/shopify/types';
import { useIntl } from 'react-intl';
import {
  CleanPricesIcon,
  DividerPriceRangeIcon,
  GoBackIcon,
  NotFoundIcon,
} from './SvgIcons';

export interface ICheckboxOption {
  handleClick: (value: boolean) => void;
  checked: boolean;
  label: string;
}

export const CheckboxOption: React.FC<ICheckboxOption> = ({
  handleClick,
  label,
  checked,
}) => (
  <FormControlLabel
    control={
      <Checkbox
        color="primary"
        checked={checked}
        onChange={event => handleClick(event.target.checked)}
        style={{ padding: '6px' }}
      />
    }
    label={label}
  />
);

export const RadioOption: React.FC<ICheckboxOption> = ({
  handleClick,
  label,
  checked,
}) => (
  <FormControlLabel
    value="bottom"
    control={
      <Radio
        color="primary"
        checked={checked}
        value={label}
        onClick={() => handleClick(true)}
        style={{ padding: '2px 1px' }}
      />
    }
    label={label}
    labelPlacement="end"
  />
);

type IFilterOption = {
  type: FilterParamsItemType;
} & ICheckboxOption;

export const FilterOption: React.FC<IFilterOption> = ({ type, ...params }) => {
  if (type === 'checkbox') return <CheckboxOption {...params} />;
  return <RadioOption {...params} />;
};

export interface IRangeOption {
  value: number[];
  max: number;
  handleChange: (value: number[]) => void;
}

export interface IPriceInput {
  value: number;
  label: string;
  handleChange: (val: number) => void;
}

export const PriceInput: React.FC<IPriceInput> = ({
  value,
  label,
  handleChange,
}) => {
  const intl = useIntl();
  const { formatNumber } = intl;
  const formattedPrice = formatNumber(value);

  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel htmlFor="outlined-adornment-amount">{label}</InputLabel>
      <OutlinedInput
        id="outlined-adornment-amount"
        value={formattedPrice}
        onChange={event =>
          handleChange(
            parseInt(event.target.value.replace(/,/g, '') || '0', 10)
          )
        }
        startAdornment={<InputAdornment position="start">$</InputAdornment>}
        labelWidth={label.length * 10}
        type="text"
        inputProps={{
          style: {
            height: '8px',
          },
        }}
        data-testid="shopify-utils-price-input"
      />
    </FormControl>
  );
};

export const RangeOption: React.FC<IRangeOption> = ({
  max,
  value,
  handleChange,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [minValue, maxValue] = value;

  const handleChangeFrom = (val: number) => {
    handleChange([val, maxValue]);
  };

  const handleChangeTo = (val: number) => {
    handleChange([minValue, val]);
  };

  const handleClean = () => {
    handleChange([0, max]);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="right"
        paddingBottom={1.5}
        fontSize={10}
      >
        <Box
          display="flex"
          style={{ cursor: 'pointer' }}
          onClick={handleClean}
          data-testid="shopify-utils-range-option-clean-button"
        >
          <Box style={{ textDecoration: 'underline', lineHeight: '120%' }}>
            {formatMessage({ id: 'Shopify.Filter.prices.clean.label' })}
          </Box>
          <Box paddingLeft={1}>{CleanPricesIcon}</Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" style={{ gap: 5 }}>
        <PriceInput
          value={minValue}
          label={formatMessage({ id: 'Shopify.Filter.prices.from' })}
          handleChange={handleChangeFrom}
        />
        {DividerPriceRangeIcon}
        <PriceInput
          value={maxValue}
          label={formatMessage({ id: 'Shopify.Filter.prices.to' })}
          handleChange={handleChangeTo}
        />
      </Box>
    </Box>
  );
};

export interface IChipOption {
  children?: React.ReactNode;
  onClick: () => void;
  selected: boolean;
  // eslint-disable-next-line react/require-default-props
  available?: boolean;
  noInventory?: boolean;
}

export const ChipOption: React.FC<IChipOption> = ({
  children,
  selected,
  noInventory,
  onClick,
}) => {
  const theme = useTheme();
  const unselectedBg = theme.palette.background.paper;
  const bgColor = !selected ? unselectedBg : '#4C33FF';
  const styleStrikethrough: CSSProperties = noInventory
    ? {
        background: `linear-gradient(to bottom right, ${bgColor} calc(50% - 1px), ${
          selected ? theme.palette.common.white : '#4C33FF'
        } , ${bgColor} calc(50% + 1px) )`,
      }
    : {};

  return (
    <Box
      border="1px solid #4C33FF"
      borderRadius="10px"
      width="max-content"
      padding="2px 8px"
      fontSize="12px"
      color={selected ? theme.palette.common.white : '#4C33FF'}
      bgcolor={bgColor}
      onClick={onClick}
      // @ts-expect-error MUI v4 types incompatible with React 18, will be fixed in MUI v5 migration
      style={{
        cursor: 'pointer',
        ...styleStrikethrough,
      }}
    >
      {children}
    </Box>
  );
};

export type VariantValue = { [key: string]: boolean };

interface IVariant {
  name: string;
  values: string[];
  strikethrough?: boolean[];
  // eslint-disable-next-line react/require-default-props
  selectedValueIndex: number;
  handleOnClick: (value: number) => void;
}
export const Variant: React.FC<IVariant> = ({
  name,
  values,
  strikethrough,
  selectedValueIndex = 0,
  handleOnClick,
}) => (
  <Box>
    <Box
      fontWeight="600"
      fontSize="12px"
      color="text.primary"
      style={{ textTransform: 'capitalize' }}
    >
      {name}:
    </Box>
    <Box display="flex" flexWrap="wrap" style={{ gap: '16px' }} paddingTop={1}>
      {values.map((val, valueIndex) => (
        <ChipOption
          key={val}
          selected={valueIndex === selectedValueIndex}
          onClick={() => handleOnClick(valueIndex)}
          noInventory={strikethrough?.[valueIndex]}
        >
          {val}
        </ChipOption>
      ))}
    </Box>
  </Box>
);

export interface ITotalAmount {
  subTotal: number;
  currency: string;
}

export const TotalAmount: React.FC<ITotalAmount> = ({ subTotal, currency }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const formattedPrice = useCurrencyFormat({ value: subTotal, currency });

  const label = formatMessage({
    id: 'Shopify.shoppingCart.subtotalAmount.label',
  });
  const otherPaymentsText = formatMessage({
    id: 'Shopify.shoppingCart.otherPayments.text',
  });

  return (
    <>
      <Divider style={{ height: '2px' }} />
      <Box padding="10px 0px 0px" display="flex">
        <Box width="50%">{label}</Box>
        <Box paddingLeft="10px">{formattedPrice}</Box>
      </Box>
      <Box fontSize={10} paddingTop={0.5} paddingBottom={1}>
        {otherPaymentsText}
      </Box>
      <Divider style={{ height: '2px' }} />
    </>
  );
};

interface INotFoundProduct {
  goBack: string;
  message: string;
  handleClickOnBack: () => void;
}

export const NotFoundProduct: React.FC<INotFoundProduct> = ({
  goBack,
  message,
  handleClickOnBack,
}) => (
  <Box>
    <Box>
      <Box
        display="flex"
        alignItems="center"
        marginLeft={3}
        marginBottom={2}
        mt={2}
      >
        <Box
          onClick={handleClickOnBack}
          padding="0px 8px"
          style={{ cursor: 'pointer' }}
        >
          {GoBackIcon}
        </Box>
        <Box color="text.primary" fontSize="14px" lineHeight="140%">
          {goBack}
        </Box>
      </Box>
    </Box>
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      textAlign="center"
      height="100%"
      paddingTop={10}
    >
      {NotFoundIcon}
      <Box color="text.primary" fontSize={16} padding={2}>
        {message}
      </Box>
    </Box>
  </Box>
);

export default CheckboxOption;
